import type { Source } from "@circle-react/types/AiSummary/Source";
import type { PostSpaceSummary } from "@circle-react/types/AiSummary/SpaceSummary";
import { Alert } from "@circleco/compass/components/Alert";

import { t } from "@/i18n-js/instance";
import { AiSummary } from "@/react/components/shared/AiSummary";
import { Sources } from "@/react/components/shared/AiSummary/Sources";
import { getAnimationDelay } from "@/react/components/Spaces/AiSummary/animations";

import { SummaryHeader } from "./SummaryHeader";
import { TopicsList } from "./TopicsList";

interface SpaceSummaryContentProps {
  allSources: Source[];
  contentCutOffMessage?: string | null;
  events?: string;
  formattedDateRange: string;
  isLoadingSpaceSummary: boolean;
  sourceName: string;
  spaceSummary?: PostSpaceSummary;
  topics: NonNullable<PostSpaceSummary["topics"]>;
}

export const SpaceSummaryContentV4 = ({
  spaceSummary,
  sourceName,
  formattedDateRange,
  isLoadingSpaceSummary,
  allSources,
  topics,
  contentCutOffMessage,
  events,
}: SpaceSummaryContentProps) => (
  <AiSummary.Content>
    <AiSummary.Header
      animate={isLoadingSpaceSummary}
      isHidden={
        !isLoadingSpaceSummary &&
        (Boolean(spaceSummary) || Boolean(contentCutOffMessage))
      }
    >
      {t("ai_spaces_summaries.generating")}
    </AiSummary.Header>

    {contentCutOffMessage && (
      <>
        <SummaryHeader
          formattedDateRange={formattedDateRange}
          sourceName={sourceName}
        />
        <Alert variant="warning" description={contentCutOffMessage} />
      </>
    )}

    {spaceSummary && (
      <>
        <SummaryHeader
          formattedDateRange={formattedDateRange}
          sourceName={sourceName}
        />
        <div
          className="animate-fade-in-slow opacity-0"
          style={{
            animationDelay: getAnimationDelay(2),
            animationFillMode: "forwards",
          }}
        >
          <AiSummary.TextWithUserLinks text={spaceSummary.overview || ""} />
        </div>

        <div className="mt-2">
          {topics.length > 0 && (
            <>
              <TopicsList topics={topics} />
              {allSources.length > 0 && (
                <div
                  className="animate-fade-in-slow mt-4 opacity-0"
                  style={{
                    animationDelay: getAnimationDelay(4, topics.length),
                    animationFillMode: "forwards",
                  }}
                >
                  <Sources sources={allSources} />
                </div>
              )}
            </>
          )}
        </div>

        {events && (
          <div
            className="animate-fade-in-slow opacity-0"
            style={{
              animationDelay: getAnimationDelay(5, topics.length),
              animationFillMode: "forwards",
            }}
          >
            <AiSummary.Section
              title={t("ai_spaces_summaries.events")}
              text={events}
            />
          </div>
        )}
      </>
    )}
  </AiSummary.Content>
);
